import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { ArrowRight, Save } from 'lucide-react';

const EditProfilePage = () => {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    reader_number: user?.reader_number || '',
    full_name: user?.full_name || '',
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    challenge: user?.challenge || 20,
  });
  const [loading, setLoading] = useState(false);
  const [challengeOptions, setChallengeOptions] = useState([20, 50, 100]);

  useEffect(() => {
    api.get('/dropdown-options/challenge')
      .then(res => setChallengeOptions(res.data?.values || [20, 50, 100]))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put('/profile', formData);
      toast.success('تم تحديث الملف الشخصي بنجاح');
      await refreshProfile();
      navigate('/profile');
    } catch (error) {
      toast.error('فشل تحديث الملف الشخصي');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="edit-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          onClick={() => navigate('/profile')}
          variant="ghost"
          className="text-accent hover:bg-muted"
        >
          <ArrowRight className="w-5 h-5" />
        </Button>
        <h1 className="text-4xl font-bold text-accent">تعديل الملف الشخصي</h1>
      </div>

      {/* Form */}
      <Card className="p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="reader_number" className="text-lg font-semibold">
              الرقم القرائي
            </Label>
            <Input
              id="reader_number"
              value={formData.reader_number}
              readOnly
              disabled
              className="mt-2 h-12 text-lg bg-muted/50 cursor-not-allowed"
              dir="ltr"
              data-testid="reader-number-input"
            />
            <p className="text-xs text-muted-foreground mt-1">الرقم القرائي لا يمكن تغييره</p>
          </div>

          <div>
            <Label htmlFor="full_name" className="text-lg font-semibold">
              الاسم الثلاثي
            </Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="الاسم الكامل"
              className="mt-2 h-12 text-lg"
            />
          </div>

          <div>
            <Label htmlFor="name" className="text-lg font-semibold">
              الاسم المختصر
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="الاسم"
              className="mt-2 h-12 text-lg"
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-lg font-semibold">
              رقم الهاتف
            </Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="96812345678"
              className="mt-2 h-12 text-lg"
              dir="ltr"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-lg font-semibold">
              البريد الإلكتروني
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="example@email.com"
              className="mt-2 h-12 text-lg"
              dir="ltr"
            />
          </div>

          <div>
            <Label htmlFor="challenge" className="text-lg font-semibold">
              التحدي القرائي
            </Label>
            <select
              id="challenge"
              value={formData.challenge}
              onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
              className="mt-2 w-full h-12 text-lg border rounded-md px-3 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="challenge-select"
            >
              {challengeOptions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/profile')}
              className="flex-1 h-12"
              disabled={loading}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              className="flex-1 h-12 bg-primary hover:bg-primary/90"
              disabled={loading}
            >
              <Save className="w-5 h-5 ml-2" />
              {loading ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditProfilePage;
